import { Button } from "@/components/ui/button";
import { useSession } from "@/containers/global/useSession";
import { useTranslation } from "@/shared/i18n";
import type { TranslationKey } from "@/shared/i18n/types";
import * as React from "react";
import { LoadingIndicator } from "./LoadingIndicator";
import { PageHero } from "./PageHero";
import { Section } from "./Section";
import { SectionHeading } from "./SectionHeading";

export interface RequireAuthProps {
  children: React.ReactNode;
  titleKey?: TranslationKey;
  bodyKey?: TranslationKey;
  /**
   * The gated page's own header photograph, so the signed-out state opens the
   * same way the signed-in one does instead of dropping the visitor onto a
   * bare cream band. Pass the identical path the page passes to its own
   * `PageHero` — `/my-farm` does.
   *
   * Left unset the prompt stays on cream, which is right for the screens that
   * have no photographic header of their own: `/admin` is internal, and the
   * checkout return is a transient status screen.
   */
  image?: string;
}

/**
 * Gate for the buyer-only screens.
 *
 * It prompts in place rather than redirecting to a login page: the visitor
 * keeps their URL, so a shared `/my-farm` link still lands where it should
 * after logging in.
 *
 * Note it renders nothing but a spinner while `isLoading` — Web3Auth v10
 * rehydrates asynchronously and deciding early would sign the user out on
 * every refresh.
 *
 * This is convenience, not security. The real boundary is `server/`, which
 * verifies the JWT on every protected endpoint.
 */
const RequireAuth = ({
  children,
  titleKey = "myFarm.loginTitle",
  bodyKey = "myFarm.loginBody",
  image,
}: RequireAuthProps) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, login } = useSession();

  if (isLoading) return <LoadingIndicator fullPageHeight />;

  if (!isAuthenticated) {
    // Same words either way; only the ground under them changes. Over a
    // photograph the button needs `outline-inverse` — the default variant
    // rests on a grey border that a picture swallows whole.
    const prompt = {
      title: t(titleKey),
      lead: t(bodyKey),
      action: (
        <Button
          size="md"
          variant={image ? "outline-inverse" : "outline"}
          onClick={() => void login()}
        >
          {t("nav.login")}
        </Button>
      ),
    };

    return image ? (
      <PageHero image={image} {...prompt} />
    ) : (
      <Section tone="cream-light" size="lg">
        <SectionHeading as="h1" {...prompt} />
      </Section>
    );
  }

  return <>{children}</>;
};

export { RequireAuth };
